import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ActiveCallScreen } from "@/components/ActiveCallScreen";
import { RateCallDialog } from "@/components/RateCallDialog";
import { RetryCallCard } from "@/components/RetryCallCard";
import { RecentCallsList } from "@/components/RecentCallsList";
import { CallerIdNudge } from "@/components/CallerIdNudge";
import { parsePhoneNumberFromString, getExampleNumber } from "libphonenumber-js/min";
import phoneExamples from "libphonenumber-js/mobile/examples";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Phone, Delete, Loader2, Wallet as WalletIcon, ChevronDown, ClipboardPaste, BookUser, Plus, X, UserPlus, Headphones, PhoneCall, Volume2, Zap, Search, Clock, ArrowLeftRight, AlertTriangle } from "lucide-react";
import { Device } from "@twilio/voice-sdk";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const KEY_LETTERS = { 2: "ABC", 3: "DEF", 4: "GHI", 5: "JKL", 6: "MNO", 7: "PQRS", 8: "TUV", 9: "WXYZ", 0: "+" };

function useDestinationTime(tz) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!tz) return null;
    try {
      const timeStr = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true,
      }).format(now);
      const hour = parseInt(
        new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", hour12: false }).format(now),
        10
      );
      const isNight = hour >= 22 || hour < 7;
      return { timeStr, isNight };
    } catch {
      return null;
    }
  }, [tz, now]);
}

export default function Dialer() {
  const { user, setBalance, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const redial = location.state?.redial;
  const [rates, setRates] = useState([]);
  const [rateId, setRateId] = useState("");
  const [number, setNumber] = useState("");
  const [starting, setStarting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [ratePrompt, setRatePrompt] = useState(null);
  const [retry, setRetry] = useState(null);
  const [recentsOpen, setRecentsOpen] = useState(false);
  const [speedDial, setSpeedDial] = useState([]);
  useEffect(() => {
    if (!retry?.call_id || retry.smsCost != null) return;
    const t = setTimeout(() => {
      api.get(`/calls/${retry.call_id}`).then(({ data }) => {
        if (data?.sms_sent) {
          setRetry((r) => (r && r.call_id === data.id ? { ...r, smsCost: data.sms_cost ?? 0 } : r));
          refreshUser();
        }
      }).catch(() => {});
    }, 4000);
    return () => clearTimeout(t);
  }, [retry?.call_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // contacts
  const [contacts, setContacts] = useState([]);
  const [contactQuery, setContactQuery] = useState("");
  const [contactsOpen, setContactsOpen] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNumber, setNewNumber] = useState("");
  const supportsPicker = typeof navigator !== "undefined" && "contacts" in navigator && navigator.contacts?.select;

  // active call state
  const [call, setCall] = useState(null);
  const [phase, setPhase] = useState("calling-you");
  const [elapsed, setElapsed] = useState(0);
  const [ending, setEnding] = useState(false);
  const timerRef = useRef(null);
  const deviceRef = useRef(null);
  const browserCallRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [loud, setLoud] = useState(false);
  const loudCtxRef = useRef(null);

  const stopLoud = () => {
    try { loudCtxRef.current?.close(); } catch { /* already closed */ }
    loudCtxRef.current = null;
    setLoud(false);
  };

  const toggleLoud = () => {
    if (loudCtxRef.current) return stopLoud();
    try {
      const stream = browserCallRef.current?.getRemoteStream?.();
      if (!stream) return toast.info("Speaker boost is available once the call connects.");
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain();
      gain.gain.value = 1.8;
      const comp = ctx.createDynamicsCompressor();
      src.connect(gain);
      gain.connect(comp);
      comp.connect(ctx.destination);
      loudCtxRef.current = ctx;
      setLoud(true);
      toast.success("Speaker boost on — call audio is louder now");
    } catch {
      toast.error("Speaker boost isn't supported on this device.");
    }
  };
  const [callMode, setCallMode] = useState(() => localStorage.getItem("zeeptel_call_mode") || "callback");
  const pickMode = (m) => { setCallMode(m); localStorage.setItem("zeeptel_call_mode", m); };
  const [countryOpen, setCountryOpen] = useState(false);
  const [popularIsos, setPopularIsos] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const callRef = useRef(null);
  useEffect(() => { callRef.current = call; }, [call]);

  useEffect(() => {
    api.get("/rates").then(({ data }) => {
      setRates(data);
      if (redial) {
        // Prefill from a history "redial": match by dial code, strip code from number
        const match =
          data.find((r) => r.dial_code === redial.dial_code && r.country === redial.country) ||
          data.find((r) => r.dial_code === redial.dial_code) ||
          data.find((r) => r.country === redial.country);
        if (match) {
          setRateId(match.id);
          const codeDigits = match.dial_code.replace(/\D/g, "");
          let digits = (redial.number || "").replace(/\D/g, "");
          if (digits.startsWith(codeDigits)) digits = digits.slice(codeDigits.length);
          setNumber(digits);
          return;
        }
      }
      const ng = data.find((r) => r.iso === "NG") || data[0];
      if (ng) setRateId(ng.id);
    });
    api.get("/contacts").then(({ data }) => setContacts(data.contacts)).catch(() => {});
    // Re-attach to a callback call still running after an accidental refresh
    api.get("/calls/active").then(({ data }) => {
      const c = data?.call;
      if (!c) return;
      setElapsed(c.elapsed_seconds || 0);
      setPhase(c.status === "ringing-user" ? "calling-you" : "connected");
      setCall(c);
      toast.info("Reconnected to your ongoing call.", { duration: 5000 });
    }).catch(() => {});
    const gi = new URLSearchParams(window.location.search).get("google_import");
    if (gi !== null) {
      window.history.replaceState({}, "", "/app");
      if (gi === "cancelled") toast.info("Google import cancelled.");
      else if (gi === "invalid" || gi === "failed") toast.error("Google import didn't complete — please try again.");
      else toast.success(`Imported ${gi} new contact${gi === "1" ? "" : "s"} from Google.`);
    }
    api.get("/calls").then(({ data }) => {
      const counts = {};
      const numCounts = {};
      (data || []).forEach((c) => {
        if (c.country) counts[c.country] = (counts[c.country] || 0) + 1;
        if (c.number) numCounts[c.number] = (numCounts[c.number] || 0) + 1;
      });
      setPopularIsos(Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([c]) => c));
      setSpeedDial(Object.entries(numCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([number, count]) => ({ number, count })));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const selectedRate = useMemo(() => rates.find((r) => r.id === rateId), [rates, rateId]);
  const destTime = useDestinationTime(selectedRate?.tz);

  const applyFullNumber = (raw) => {
    let s = (raw || "").trim().replace(/[^\d+]/g, "");
    if (s.startsWith("00")) s = "+" + s.slice(2);
    const only = s.replace(/\D/g, "");
    if (only.length < 5) {
      toast.error("That doesn't look like a phone number");
      return;
    }
    const matchIntl = () => {
      const sorted = [...rates].sort((a, b) => b.dial_code.length - a.dial_code.length);
      return sorted.find((r) => only.startsWith(r.dial_code.replace(/\D/g, "")));
    };
    // Only auto-detect the country when the number is clearly international:
    // starts with +/00, or is long enough (11+ digits) to include a country code.
    // Local numbers (leading 0 or ≤10 digits) keep the selected country.
    if (s.startsWith("+") || (!only.startsWith("0") && only.length >= 11)) {
      const match = matchIntl();
      if (match) {
        setRateId(match.id);
        setNumber(only.slice(match.dial_code.replace(/\D/g, "").length).replace(/^0+/, ""));
      } else {
        // Unknown country code — block dialing until the user explicitly picks a country,
        // otherwise the old selection's code would be glued on and call a stranger.
        setRateId("");
        setNumber(only.replace(/^0+/, ""));
        toast.error("We couldn't match this number to a supported country — please pick the destination above before calling");
      }
    } else {
      let digits = only.replace(/^0+/, "");
      if (selectedRate) {
        const cd = selectedRate.dial_code.replace(/\D/g, "");
        // Pasted number already includes the selected country's code — strip the duplicate
        if (digits.startsWith(cd) && digits.length >= cd.length + 8) digits = digits.slice(cd.length).replace(/^0+/, "");
      }
      setNumber(digits);
      if (selectedRate) {
        toast.info(`Local number detected — using ${selectedRate.country} (${selectedRate.dial_code}). Change the country above if that's wrong.`);
      } else {
        toast.info("Local number detected — please pick the country above");
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pasteNumber = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text || text.replace(/\D/g, "").length < 5) {
        toast.error("No phone number found in your clipboard");
        return;
      }
      applyFullNumber(text);
    } catch {
      toast.error("Clipboard access was blocked — press Ctrl/Cmd+V instead");
    }
  };

  useEffect(() => {
    const handler = (e) => {
      const t = e.clipboardData?.getData("text") || "";
      const inField = e.target instanceof Element && e.target.closest("input, textarea");
      if (t.replace(/\D/g, "").length >= 5 && !inField) {
        e.preventDefault();
        applyFullNumber(t);
      }
    };
    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
    // eslint-disable-next-line
  }, [rates, selectedRate]);

  const importFromPhone = async () => {
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: true });
      const list = picked.flatMap((c) =>
        (c.tel || []).slice(0, 1).map((t) => ({ name: (c.name || ["Unnamed"])[0] || "Unnamed", number: t }))
      );
      if (!list.length) return;
      const { data } = await api.post("/contacts", { contacts: list });
      setContacts(data.contacts);
      toast.success(`Imported ${data.added} new contact${data.added === 1 ? "" : "s"}`);
    } catch {
      /* user cancelled the picker */
    }
  };

  const importFromFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      let list = [];
      if (/BEGIN:VCARD/i.test(text)) {
        for (const card of text.split(/BEGIN:VCARD/i).slice(1)) {
          const name = (card.match(/^FN[^:]*:(.+)$/im) || [])[1]?.trim();
          const tel = (card.match(/^TEL[^:]*:(.+)$/im) || [])[1]?.trim();
          if (name && tel) list.push({ name, number: tel });
        }
      } else {
        const rows = text.split(/\r?\n/).filter((l) => l.trim());
        let ni = 0, pi = 1;
        const head = rows[0].toLowerCase();
        if (/name|phone|number|tel|mobile/.test(head)) {
          const cols = rows[0].split(",").map((c) => c.trim().toLowerCase());
          ni = Math.max(0, cols.findIndex((c) => c.includes("name")));
          pi = Math.max(1, cols.findIndex((c) => /phone|number|tel|mobile/.test(c)));
          rows.shift();
        }
        for (const row of rows) {
          const cols = row.split(",");
          const name = (cols[ni] || "").replace(/"/g, "").trim();
          const number = (cols[pi] || "").replace(/"/g, "").trim();
          if (name && number.replace(/\D/g, "").length >= 5) list.push({ name, number });
        }
      }
      list = list.slice(0, 500);
      if (!list.length) return toast.error("No contacts found in that file — expected a CSV (name, phone) or a .vcf export");
      let saved = null;
      for (let i = 0; i < list.length; i += 100) {
        const { data } = await api.post("/contacts", { contacts: list.slice(i, i + 100) });
        saved = data;
      }
      if (saved) setContacts(saved.contacts);
      toast.success(`Imported ${list.length} contact${list.length > 1 ? "s" : ""}!`);
    } catch {
      toast.error("Couldn't read that file — try a .csv or .vcf export");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addManualContact = async () => {
    if (!newName.trim() || newNumber.replace(/\D/g, "").length < 5) {
      toast.error("Enter a name and a full number with country code, e.g. +256701234567");
      return;
    }
    try {
      const { data } = await api.post("/contacts", { contacts: [{ name: newName, number: newNumber }] });
      setContacts(data.contacts);
      setNewName("");
      setNewNumber("");
      setShowAddContact(false);
      toast.success("Contact saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const removeContact = async (id) => {
    setContacts((c) => c.filter((x) => x.id !== id));
    api.delete(`/contacts/${id}`).catch(() => {});
  };

  const fullNumber = selectedRate ? `${selectedRate.dial_code} ${number}` : number;
  const e164 = useMemo(() => {
    if (!selectedRate) return number;
    const cd = selectedRate.dial_code.replace(/\D/g, "");
    let digits = number.replace(/\D/g, "").replace(/^0+/, "");
    // Typed the country code on the keypad too? Strip the duplicate so we never dial +256256...
    if (digits.startsWith(cd) && digits.length >= cd.length + 8) digits = digits.slice(cd.length).replace(/^0+/, "");
    return `+${cd}${digits}`;
  }, [selectedRate, number]);

  const runningCost = useMemo(() => {
    if (!call) return 0;
    const minutes = Math.max(1, Math.ceil(elapsed / 60));
    return minutes * call.rate;
  }, [call, elapsed]);

  const fmtTalk = (secs) => (secs >= 60 ? `${Math.floor(secs / 60)} min ${secs % 60 ? `${secs % 60} sec` : ""}`.trim() : `${secs} sec`);

  const announceTalkTime = (data) => {
    if (data.max_seconds > 0) {
      toast.info(`You have up to ${fmtTalk(data.max_seconds)} of talk time for this call at $${data.rate.toFixed(3)}/min — we'll warn you before it runs out.`, { duration: 8000 });
    }
  };

  const finalizeCall = (data, auto = false) => {
    const cur = callRef.current || call;
    if (typeof data.balance === "number") setBalance(data.balance);
    if (data.status === "failed") {
      toast.error(data.fail_reason || "The call could not be connected.");
      toast.info("You were not charged for this call.", { duration: 6000 });
      if (cur) setRetry({ number: cur.number, reason: data.fail_reason, mode: cur.mode, call_id: cur.call_id });
    } else {
      setNumber("");
      const secs = data.duration_seconds ?? 0;
      const talkTime = fmtTalk(secs);
      if (secs === 0 || !data.cost) {
        toast.success("Call ended — you were not charged for this call.", { duration: 7000 });
      } else {
        toast.success(`Call ended • ${talkTime} talk time • cost $${data.cost.toFixed(2)}${auto ? " (balance limit)" : ""}`, { duration: 8000 });
        if (cur && cur.rate > 0 && typeof data.balance === "number") {
          const minsLeft = Math.floor(data.balance / cur.rate);
          toast.info(`$${data.balance.toFixed(2)} airtime left — about ${minsLeft} more minute${minsLeft === 1 ? "" : "s"} to ${cur.country}.`, { duration: 8000 });
        }
      }
      if (secs > 0 && cur) {
        setRatePrompt({ call_id: cur.call_id, country: cur.country, flag: cur.flag, talkTime });
      }
    }
    setCall(null);
    setElapsed(0);
    stopLoud();
    refreshUser();
  };

  useEffect(() => {
    if (!call) return undefined;

    if (call.mode === "twilio" || call.mode === "browser") {
      // Live call: run a local timer and poll backend for completion/billing
      timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/calls/${call.call_id}`);
          if (data.status === "completed" || data.status === "failed") {
            clearInterval(timerRef.current);
            clearInterval(pollRef.current);
            if (call.mode === "browser" && browserCallRef.current) browserCallRef.current.disconnect();
            else finalizeCall(data);
          } else if (data.phase && call.mode !== "browser") {
            setPhase(data.phase);
          }
        } catch (e) {
          /* keep polling */
        }
      }, 3000);
    } else {
      // Simulated: count up locally and auto-end at balance limit
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          const next = prev + 1;
          if (call.max_seconds && next >= call.max_seconds) {
            endCall(true);
            return prev;
          }
          return next;
        });
      }, 1000);
    }
    return () => {
      clearInterval(timerRef.current);
      clearInterval(pollRef.current);
    };
  }, [call]); // eslint-disable-line

  const press = (k) => setNumber((n) => n + k);
  const backspace = () => setNumber((n) => n.slice(0, -1));

  const requestCall = () => {
    if (!selectedRate) return toast.error("Select a destination country");
    if (number.replace(/\D/g, "").length < 4) {
      const last = localStorage.getItem("zanitel_last_dialed");
      if (last) {
        applyFullNumber(last);
        toast.info("Last number loaded — press Call again to dial");
        return;
      }
      return toast.error("Enter a valid phone number");
    }
    // Safety net: a full international number typed on the keypad with the wrong
    // country selected would otherwise dial a stranger (+1 + 256752... = Alabama).
    const digits = number.replace(/\D/g, "");
    const cd = selectedRate.dial_code.replace(/\D/g, "");
    if (!digits.startsWith("0") && digits.length >= 11 && !digits.startsWith(cd)) {
      const sorted = [...rates].sort((a, b) => b.dial_code.length - a.dial_code.length);
      const match = sorted.find((r) => digits.startsWith(r.dial_code.replace(/\D/g, "")));
      if (match && match.id !== rateId) {
        setRateId(match.id);
        setNumber(digits.slice(match.dial_code.replace(/\D/g, "").length).replace(/^0+/, ""));
        toast.warning(
          `This number starts with ${match.dial_code} (${match.country}) — we switched the destination for you. Check the number and press Call again.`,
          { duration: 8000 }
        );
        return;
      }
    }
    startCall();
  };

  const startCall = async (modeOverride) => {
    setConfirming(false);
    setRetry(null);
    const mode = typeof modeOverride === "string" ? modeOverride : callMode;
    if (mode === "browser") return startBrowserCall();
    setStarting(true);
    try {
      const { data } = await api.post("/calls/start", { rate_id: rateId, number: e164 });
      localStorage.setItem("zanitel_last_dialed", e164);
      setElapsed(0);
      setPhase("calling-you");
      setCall(data);
      toast.success(`Connecting to ${data.country} ${data.flag}`);
      announceTalkTime(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!detail) {
        // Response was lost (network/proxy hiccup) — the call may still have started.
        // Retry re-attaching for up to ~9s before showing an error.
        for (let attempt = 0; attempt < 5; attempt++) {
          try {
            await new Promise((r) => setTimeout(r, 1800));
            const { data: recent } = await api.get("/calls");
            const c = (recent || []).find(
              (x) => ["ringing-user", "in-progress"].includes(x.status) &&
                     Date.now() - new Date(x.started_at).getTime() < 45000
            );
            if (c) {
              localStorage.setItem("zanitel_last_dialed", e164);
              setElapsed(0);
              setPhase("calling-you");
              setCall({
                call_id: c.id, status: c.status, rate: c.rate, country: c.country, flag: c.flag,
                number: c.number, max_seconds: c.max_seconds, mode: c.mode,
                user_phone: c.user_phone, callback_from: c.callback_from || "",
              });
              toast.success(`Connecting to ${c.country} ${c.flag}`);
              setStarting(false);
              return;
            }
          } catch { /* keep retrying */ }
        }
      }
      toast.error(apiError(detail));
    } finally {
      setStarting(false);
    }
  };

  const startBrowserCall = async () => {
    setStarting(true);
    let data = null;
    try {
      const resp = await api.post("/calls/start", { rate_id: rateId, number: e164, channel: "browser" });
      data = resp.data;
      localStorage.setItem("zanitel_last_dialed", e164);
      // Show the in-call screen (with End button) IMMEDIATELY, before any device setup
      setElapsed(0);
      setMuted(false);
      setPhase("ringing-destination");
      setCall(data);
      const device = new Device(data.token, { logLevel: "silent" });
      deviceRef.current = device;
      device.on("error", (e) => {
        toast.error(e?.message ? `Call error: ${e.message}${e.code ? ` (code ${e.code})` : ""}` : "Call error — please try again", { duration: 9000 });
      });
      const c = await device.connect({ params: { call_id: data.call_id } });
      browserCallRef.current = c;
      c.on("accept", () => setPhase("connected"));
      c.on("error", (e) => toast.error(e?.message || "Call error"));
      c.on("disconnect", () => {
        browserCallRef.current = null;
        clearInterval(timerRef.current);
        clearInterval(pollRef.current);
        try { device.destroy(); } catch { /* already gone */ }
        deviceRef.current = null;
        setTimeout(async () => {
          try {
            const { data: fin } = await api.get(`/calls/${data.call_id}`);
            finalizeCall(fin, false);
          } catch { setCall(null); setElapsed(0); }
          setEnding(false);
        }, 2000);
      });
      toast.success(`Calling ${data.country} ${data.flag} from this device`);
      announceTalkTime(data);
    } catch (err) {
      // Setup/permission failed — tear down cleanly so the user is never stuck
      try { deviceRef.current?.destroy(); } catch { /* noop */ }
      deviceRef.current = null;
      browserCallRef.current = null;
      setCall(null);
      setElapsed(0);
      if (data?.call_id) api.post(`/calls/${data.call_id}/end`).catch(() => {});
      const detail = err.response?.data?.detail;
      const msg = String(err?.message || err);
      const code = err?.code;
      let friendly;
      if (/denied|permission|NotAllowed|getUserMedia|microphone/i.test(msg)) {
        friendly = "Microphone access was blocked — click the 🔒 icon in your address bar, allow the Microphone, then try again";
      } else if (/NotFound|AcquisitionFailed|no.*audio.*input/i.test(msg) || code === 31402) {
        friendly = "No microphone was found on this device — plug in a mic/headset or use \"Ring my phone\" mode";
      } else if (code === 31000 || code === 31005 || /websocket|network|ice/i.test(msg)) {
        friendly = "Your network is blocking internet calls — try another Wi-Fi/hotspot or use \"Ring my phone\" mode";
      } else if (detail) {
        friendly = apiError(detail);
      } else {
        friendly = `Could not start the call: ${msg}${code ? ` (code ${code})` : ""}`;
      }
      toast.error(friendly, { duration: 9000 });
    } finally {
      setStarting(false);
    }
  };

  const endCall = async (auto = false) => {
    if (!call || ending) return;
    if (call.mode === "browser") {
      setEnding(true);
      if (browserCallRef.current) browserCallRef.current.disconnect();
      else { setCall(null); setEnding(false); }
      return;
    }
    setEnding(true);
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
    try {
      const { data } = await api.post(`/calls/${call.call_id}/end`);
      finalizeCall(data, auto);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setEnding(false);
    }
  };

  const lowBalance = (user?.balance ?? 0) < (selectedRate?.rate ?? 0);

  const dialedDigits = number.replace(/\D/g, "");
  const numberHint = useMemo(() => {
    if (!selectedRate || dialedDigits.length < 6) return null;
    const parsed = parsePhoneNumberFromString(e164);
    if (parsed?.isValid()) return null;
    const ex = selectedRate.iso ? getExampleNumber(selectedRate.iso, phoneExamples) : null;
    const expected = ex ? ex.nationalNumber.length : null;
    const typed = e164.replace(/\D/g, "").length - selectedRate.dial_code.replace(/\D/g, "").length;
    return expected
      ? `${selectedRate.country} numbers have ${expected} digits after ${selectedRate.dial_code} — you've typed ${typed}.`
      : `This doesn't look like a valid ${selectedRate.country} number.`;
  }, [selectedRate, e164, dialedDigits.length]);
  const matchedContact = useMemo(() => {
    if (dialedDigits.length < 6) return null;
    const full = e164.replace(/\D/g, "");
    return contacts.find((c) => {
      const cd = (c.number || "").replace(/\D/g, "");
      return cd && (cd === full || cd.endsWith(full.slice(-9)) || full.endsWith(cd.slice(-9)));
    });
  }, [contacts, dialedDigits, e164]);

  const rateForNumber = (num) => {
    const digits = (num || "").replace(/\D/g, "");
    if (!digits) return null;
    return [...rates]
      .filter((r) => digits.startsWith(r.dial_code.replace(/\D/g, "")))
      .sort((a, b) => b.dial_code.length - a.dial_code.length)[0] || null;
  };

  const filteredContacts = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return contacts;
    const qd = q.replace(/\D/g, "");
    return contacts.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (qd && (c.number || "").replace(/\D/g, "").includes(qd))
    );
  }, [contacts, contactQuery]);

  // ---------------- In-call screen ----------------
  if (call) {
    return (
      <ActiveCallScreen
        call={call}
        phase={phase}
        contactName={matchedContact?.name}
        elapsed={elapsed}
        ending={ending}
        runningCost={runningCost}
        onEnd={() => endCall(false)}
        muted={muted}
        onToggleMute={call.mode === "browser" ? () => {
          const next = !muted;
          browserCallRef.current?.mute(next);
          setMuted(next);
        } : undefined}
        onSendDigit={call.mode === "browser" ? (d) => {
          try { browserCallRef.current?.sendDigits(d); } catch { /* not connected yet */ }
        } : undefined}
        loud={loud}
        onToggleLoud={call.mode === "browser" ? toggleLoud : undefined}
      />
    );
  }

  // ---------------- Dialer screen ----------------
  const numberField = (suffix, extraCls = "") => {
    const digitsLen = number.replace(/\D/g, "").length;
    const sizeCls = digitsLen > 11 ? "text-[13px] sm:text-lg tracking-normal" : digitsLen > 8 ? "text-[15px] sm:text-xl tracking-normal" : "text-xl sm:text-2xl tracking-wider";
    return (
    <div className={`bg-white rounded-xl border border-black/10 px-3 py-2 min-h-[52px] flex items-center gap-2 ${extraCls}`}>
      {selectedRate && (
        <span className={`shrink-0 font-mono-dial ${sizeCls} text-zinc-400`} data-testid={`dialer-dialcode-prefix${suffix}`}>
          {selectedRate.dial_code}
        </span>
      )}
      <input
        type="tel"
        inputMode="tel"
        value={number}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw.trim().startsWith("+") || raw.trim().startsWith("00")) {
            if (raw.replace(/\D/g, "").length >= 5) {
              applyFullNumber(raw);
            } else {
              setNumber(raw.replace(/[^\d+]/g, ""));
            }
            return;
          }
          setNumber(raw.replace(/\D/g, ""));
        }}
        onPaste={(e) => {
          const t = e.clipboardData?.getData("text") || "";
          if (t.replace(/\D/g, "").length >= 5) {
            e.preventDefault();
            applyFullNumber(t);
          }
        }}
        placeholder={selectedRate ? "Type or paste a number" : "Pick a country first"}
        aria-label="Phone number"
        data-testid={`dialer-display${suffix}`}
        className={`flex-1 min-w-0 bg-transparent outline-none border-0 font-mono-dial ${sizeCls} text-zinc-900 placeholder:text-zinc-300 placeholder:text-base`}
      />
      <button
        onClick={pasteNumber}
        data-testid={`dialer-paste-button${suffix}`}
        title="Paste a number"
        className={`shrink-0 h-9 w-9 rounded-lg bg-blue-50 border border-blue-100 items-center justify-center text-primary hover:bg-blue-100 transition-colors ${digitsLen > 8 ? "hidden sm:flex" : "flex"}`}
      >
        <ClipboardPaste className="h-4 w-4" />
      </button>
    </div>
    );
  };

  const pickContact = (num) => { applyFullNumber(num); setContactsOpen(false); };

  const contactsBody = (
      <div data-testid="dialer-contacts">
        <div className="relative mb-3" data-testid="contacts-search-wrap">
          <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={contactQuery}
            onChange={(e) => setContactQuery(e.target.value)}
            placeholder="Search by name or number…"
            data-testid="contacts-search-input"
            className="w-full bg-zinc-50 border border-black/10 rounded-xl pl-9 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
          />
          {contactQuery && (
            <button
              onClick={() => setContactQuery("")}
              data-testid="contacts-search-clear"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-zinc-400 hover:text-zinc-700 flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mb-3">
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".csv,.vcf,text/csv,text/vcard" className="hidden"
              onChange={(e) => importFromFile(e.target.files?.[0])} data-testid="contacts-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="contacts-import-file"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/5 border border-primary/30 text-primary text-xs font-bold hover:bg-primary/10 transition-colors"
            >
              <ClipboardPaste className="h-3.5 w-3.5" /> Upload CSV/vCard
            </button>
            {supportsPicker && (
              <button
                onClick={importFromPhone}
                data-testid="contacts-import-phone"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/5 border border-primary/30 text-primary text-xs font-bold hover:bg-primary/10 transition-colors"
              >
                <UserPlus className="h-3.5 w-3.5" /> From phone
              </button>
            )}
            <button
              onClick={async () => {
                try {
                  const { data } = await api.get("/google-contacts/auth-url");
                  window.location.href = data.url;
                } catch (err) {
                  toast.error(apiError(err.response?.data?.detail));
                }
              }}
              data-testid="contacts-import-google"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/5 border border-primary/30 text-primary text-xs font-bold hover:bg-primary/10 transition-colors"
            >
              <BookUser className="h-3.5 w-3.5" /> Google
            </button>
            <button
              onClick={() => setShowAddContact(!showAddContact)}
              data-testid="contacts-add-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-zinc-50 border border-black/10 text-zinc-600 text-xs font-bold hover:border-primary/40 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        </div>

        {showAddContact && (
          <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="contacts-add-form">
            <input
              value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name"
              data-testid="contacts-add-name"
              className="flex-1 bg-zinc-50 border border-black/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              value={newNumber} onChange={(e) => setNewNumber(e.target.value)} placeholder="+256 701 234 567"
              type="tel" data-testid="contacts-add-number"
              className="flex-1 bg-zinc-50 border border-black/10 rounded-xl px-3 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={addManualContact}
              data-testid="contacts-add-save"
              className="px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90"
            >
              Save
            </button>
          </div>
        )}

        {contacts.length === 0 ? (
          <p className="text-sm text-zinc-400 py-3 text-center">
            No contacts yet — {supportsPicker ? "import from your phone or " : ""}add one, then call with a single tap.
          </p>
        ) : filteredContacts.length === 0 ? (
          <p className="text-sm text-zinc-400 py-3 text-center" data-testid="contacts-no-match">
            No contacts match "{contactQuery}".
          </p>
        ) : (
          <div className="divide-y divide-black/5 overflow-y-auto -mx-2">
            {filteredContacts.map((c) => (
              <div key={c.id} className="flex items-center gap-3 px-2 py-2.5" data-testid={`contact-row-${c.id}`}>
                <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-xs text-zinc-400 font-mono truncate">{c.number}</div>
                  {(() => {
                    const r = rateForNumber(c.number);
                    if (!r) return null;
                    const mins = r.rate > 0 ? Math.floor((user?.balance ?? 0) / r.rate) : 0;
                    return (
                      <div className="text-[11px] text-zinc-500 mt-0.5 truncate" data-testid={`contact-rate-${c.id}`}>
                        {r.flag} ${r.rate.toFixed(2)}/min · {mins > 0 ? `${mins} min on your balance` : "top up to call"}
                      </div>
                    );
                  })()}
                </div>
                <button
                  onClick={() => pickContact(c.number)}
                  data-testid={`contact-dial-${c.id}`}
                  title={`Dial ${c.name}`}
                  className="h-9 w-9 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center hover:bg-emerald-100 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                </button>
                <button
                  onClick={() => removeContact(c.id)}
                  data-testid={`contact-delete-${c.id}`}
                  title="Remove contact"
                  className="h-8 w-8 rounded-full text-zinc-300 hover:text-red-500 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
  );

  return (
    <div className={`mx-auto max-w-md ${location.state?.slideIn ? "animate-in slide-in-from-right-8 fade-in duration-300" : ""}`}>
      <RateCallDialog prompt={ratePrompt} onClose={() => setRatePrompt(null)} />
      <div className="min-w-0">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-heading text-xl font-bold tracking-tight">Dialer</h1>
        <Link to="/wallet" className="flex items-center gap-1.5 text-sm font-bold text-primary bg-accent px-3 py-1.5 rounded-full" data-testid="dialer-topup-link">
          <WalletIcon className="h-3.5 w-3.5" /> ${(user?.balance ?? 0).toFixed(2)}
        </Link>
      </div>

      <CallerIdNudge user={user} />

      <RetryCallCard
        retry={retry}
        busy={starting}
        onDismiss={() => setRetry(null)}
        onRetry={() => startCall()}
        onSwitch={() => {
          const next = retry.mode === "browser" ? "callback" : "browser";
          pickMode(next);
          startCall(next);
        }}
      />

      <Sheet open={contactsOpen} onOpenChange={setContactsOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto sm:max-w-lg sm:mx-auto" data-testid="contacts-sheet">
          <SheetHeader className="text-left mb-3">
            <SheetTitle className="font-heading flex items-center gap-2"><BookUser className="h-4 w-4 text-primary" /> My contacts <span className="text-xs text-zinc-400 font-normal">({contacts.length})</span></SheetTitle>
          </SheetHeader>
          {contactsBody}
        </SheetContent>
      </Sheet>

      <Sheet open={recentsOpen} onOpenChange={setRecentsOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto sm:max-w-lg sm:mx-auto" data-testid="recents-sheet">
          <SheetHeader className="text-left mb-3">
            <SheetTitle className="font-heading flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Recent calls</SheetTitle>
          </SheetHeader>
          <div data-testid="dialer-recents">
            <RecentCallsList
              contacts={contacts}
              onPick={(c) => { applyFullNumber(c.number); setRecentsOpen(false); }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <div data-testid="keypad-section">
      <div className="bg-gradient-to-b from-white to-blue-50/60 border border-black/10 rounded-2xl p-4 shadow-sm">
        {/* Country selector — searchable with popular pins */}
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <button
              className="w-full h-11 rounded-xl border border-black/10 bg-white mb-2.5 text-base px-3 flex items-center justify-between gap-2 text-left"
              data-testid="country-select-trigger"
            >
              {selectedRate ? (
                <span className="flex items-center gap-2 truncate">
                  <span className="text-lg">{selectedRate.flag}</span>
                  <span className="font-medium truncate">{selectedRate.country}</span>
                  <span className="text-zinc-400 font-mono text-xs">{selectedRate.dial_code}</span>
                </span>
              ) : (
                <span className="text-zinc-400">Select destination</span>
              )}
              <ChevronDown className="h-4 w-4 text-zinc-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
            <Command>
              <CommandInput placeholder={`Search ${rates.length} countries…`} data-testid="country-search-input" />
              <CommandList className="max-h-72">
                <CommandEmpty>No country found.</CommandEmpty>
                {popularIsos.length > 0 && (
                  <CommandGroup heading="Your favorites">
                    {popularIsos.map((cn) => {
                      const r = rates.find((x) => x.country === cn);
                      if (!r) return null;
                      return (
                        <CommandItem key={`pop-${r.id}`} value={`zz-${r.country}`}
                          onSelect={() => { setRateId(r.id); setCountryOpen(false); }}
                          data-testid={`country-popular-${r.iso}`}>
                          <span className="flex items-center gap-2 w-full">
                            <span className="text-lg">{r.flag}</span>
                            <span className="font-medium">{r.country}</span>
                            <span className="text-zinc-400 font-mono text-xs ml-auto">{r.dial_code}</span>
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
                <CommandGroup heading={`All countries (${rates.length})`}>
                  {rates.map((r) => (
                    <CommandItem key={r.id} value={`${r.country} ${r.dial_code}`}
                      onSelect={() => { setRateId(r.id); setCountryOpen(false); }}
                      data-testid={`country-option-${r.iso}`}>
                      <span className="flex items-center gap-2 w-full">
                        <span className="text-lg">{r.flag}</span>
                        <span className="font-medium">{r.country}</span>
                        <span className="text-zinc-400 font-mono text-xs ml-auto">{r.dial_code}</span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Number display — fully editable: type, select, copy & long-press paste */}
        {numberField("", "mb-2")}

        <div className="grid grid-cols-2 gap-2 mb-3" data-testid="dialer-quick-row">
          <button type="button" onClick={() => setContactsOpen(true)} data-testid="contacts-open-button"
            className="h-10 rounded-xl bg-white border border-black/10 hover:border-primary/40 text-sm font-semibold text-zinc-700 flex items-center justify-center gap-2 active:scale-95 transition">
            <BookUser className="h-4 w-4 text-primary" /> Contacts <span className="text-xs text-zinc-400 font-normal">({contacts.length})</span>
          </button>
          <button type="button" onClick={() => setRecentsOpen(true)} data-testid="recents-open-button"
            className="h-10 rounded-xl bg-white border border-black/10 hover:border-primary/40 text-sm font-semibold text-zinc-700 flex items-center justify-center gap-2 active:scale-95 transition">
            <Clock className="h-4 w-4 text-primary" /> Recents
          </button>
        </div>

        {/* Rate + destination time, one compact row */}
        {selectedRate && (
          <div
            key={selectedRate.id}
            className={`flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-xl border ${
              destTime?.isNight
                ? "bg-indigo-50 border-indigo-200 text-indigo-900"
                : "bg-blue-50 border-blue-100 text-blue-900"
            }`}
            data-testid="destination-local-time"
          >
            <div className="min-w-0" data-testid="rate-preview">
              <div className="text-xs sm:text-sm font-semibold truncate">
                {selectedRate.flag} {selectedRate.country}
              </div>
              <div className="font-mono text-[11px] sm:text-xs font-bold text-primary whitespace-nowrap">${selectedRate.rate.toFixed(3)}/min</div>
            </div>
            {destTime && (
              <div className="text-right shrink-0">
                <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest opacity-70 whitespace-nowrap">
                  {destTime.isNight ? "🌙 their time" : "☀️ their time"}
                </div>
                <div className="time-flash font-mono font-bold text-lg sm:text-2xl leading-none" data-testid="destination-time-value">
                  {destTime.timeStr}
                </div>
                {destTime.isNight && (
                  <div className="text-[9px] sm:text-[10px] font-semibold text-indigo-700">they may be asleep</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Speed dial — 3 most-called numbers */}
        {speedDial.length > 0 && (
          <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar" data-testid="speed-dial-row">
            {speedDial.map((s) => {
              const c = contacts.find((x) => (x.number || "").replace(/\D/g, "") === s.number.replace(/\D/g, ""));
              const r = rateForNumber(s.number);
              return (
                <button key={s.number} type="button" onClick={() => applyFullNumber(s.number)} title={`Dial ${s.number}`}
                  data-testid={`speed-dial-${s.number.replace(/\D/g, "")}`}
                  className="flex items-center gap-1.5 shrink-0 max-w-[46%] rounded-full bg-white border border-emerald-200 hover:bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-zinc-700 active:scale-95 transition">
                  <Zap className="h-3 w-3 text-emerald-500 shrink-0" />
                  {r?.flag && <span className="shrink-0">{r.flag}</span>}
                  <span className={`truncate ${c ? "" : "font-mono"}`}>{c ? c.name : s.number}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {KEYS.map((k) => (
            <button
              key={k}
              onClick={() => press(k)}
              data-testid={`dialer-key-${k}`}
              className="h-14 rounded-2xl bg-white hover:bg-blue-50 active:bg-blue-100 border border-slate-200 hover:border-blue-200 shadow-sm transition-colors active:scale-95 flex flex-col items-center justify-center leading-none"
            >
              <span className="font-mono-dial text-2xl font-light tracking-wide text-slate-900">{k}</span>
              {KEY_LETTERS[k] && <span className="text-[8px] font-bold tracking-[0.25em] text-slate-500 mt-0.5">{KEY_LETTERS[k]}</span>}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={requestCall}
            disabled={starting}
            data-testid="call-submit-button"
            className="flex-1 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 transition-transform active:scale-95 disabled:opacity-60"
          >
            {starting ? <Loader2 className="h-6 w-6 animate-spin" /> : <Phone className="h-6 w-6" />} Call
          </button>
          <button
            onClick={backspace}
            data-testid="dialer-backspace"
            className="h-14 w-14 shrink-0 rounded-full bg-white hover:bg-red-50 border border-slate-200 hover:border-red-200 flex items-center justify-center transition-colors active:scale-95"
          >
            <Delete className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <button
          onClick={() => navigate("/connect")}
          data-testid="mode-pill-button"
          className="mt-3 mx-auto flex items-center gap-2 rounded-full bg-white border border-black/10 px-3.5 py-1.5 text-xs font-semibold text-zinc-600 hover:border-primary/40 active:scale-95 transition"
        >
          {callMode === "browser" ? <Headphones className="h-3.5 w-3.5 text-primary" /> : <PhoneCall className="h-3.5 w-3.5 text-primary" />}
          {callMode === "browser" ? "Calling from this device" : "We'll ring your phone"}
          <span className="text-zinc-400 flex items-center gap-0.5"><ArrowLeftRight className="h-3 w-3" /> change</span>
        </button>

        {numberHint && (
          <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3.5 py-2.5 text-xs text-red-700 font-medium" data-testid="number-invalid-hint">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-px" /> {numberHint}
          </div>
        )}

        {lowBalance && (
          <div className="mt-4 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="low-balance-warning">
            <span className="text-sm text-amber-800 font-medium">Balance too low for this destination.</span>
            <Link to="/wallet" className="text-sm font-bold text-amber-900 underline whitespace-nowrap">Top up</Link>
          </div>
        )}

        {confirming && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="call-confirm-dialog" onClick={() => setConfirming(false)}>
            <div className="bg-white rounded-3xl p-6 w-full max-w-sm text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="text-4xl mb-2">{selectedRate?.flag}</div>
              <div className="text-sm text-zinc-500 font-medium">{selectedRate?.country} · ${selectedRate?.rate?.toFixed(3)}/min</div>
              <div className="font-mono-dial text-2xl font-bold mt-1 mb-1 break-all" data-testid="call-confirm-number">{e164}</div>
              <p className="text-xs text-zinc-400 mb-5">Your phone will ring first — answer it and we'll connect you.</p>
              <div className="space-y-2.5">
                <button
                  onClick={startCall}
                  data-testid="call-confirm-button"
                  className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 transition-colors active:scale-95"
                >
                  <Phone className="h-5 w-5" /> Confirm call to {e164}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  data-testid="call-decline-button"
                  className="w-full h-12 rounded-full bg-zinc-100 hover:bg-red-50 text-zinc-700 hover:text-red-700 font-bold transition-colors active:scale-95"
                >
                  Decline call to {e164}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      </div>
    </div>
  );
}
